import Decimal from 'break_infinity.js'

export { Decimal }

export type BuildingId =
  | 'codingAssistant'
  | 'codingAgent'
  | 'laptopFarm'
  | 'modelWrapper'
  | 'pearPro'
  | 'gpu'
  | 'cloudCluster'
  | 'tpu'
  | 'serverRack'
  | 'customModel'
  | 'datacenter'
  | 'atomicEnergy'
  | 'hyperscaler'
  | 'quantumCompute'
  | 'dysonSphere'

export type BuildingDefinition = {
  readonly id: BuildingId
  readonly name: string
  readonly baseCost: Decimal
  readonly baseProduction: Decimal
  readonly description: string
}

export type UpgradeId =
  | 'mechanicalKeyboard'
  | 'dualMonitors'
  | 'ultrawideUpgrade'
  | 'standingDesk'
  | 'rgbEverything'
  | 'carpalTunnelBrace'
  | 'betterPrompts'
  | 'longerContext'
  | 'customInstructions'
  | 'multiModelEnsemble'
  | 'copilotCartel'
  | 'upgradeToSonnet4'
  | 'upgradeToOpus5'
  | 'autonomousLoops'
  | 'devContainers'
  | 'specDrivenDevelopment'
  | 'localKnowledge'
  | 'mSeriesChips'
  | 'betterThermals'
  | 'laptopStacking'
  | 'stolenWifi'
  | 'distributedTraining'
  | 'usedRax3090'
  | 'newRax5090'
  | 'overclock'
  | 'waterCooling'
  | 'cudaOptimization'
  | 'modelDistillation'
  | 'miningRigConversion'
  | 'multiGpuRig'
  | 'spotInstances'
  | 'reservedCapacity'
  | 'multiRegion'
  | 'kubernetes'
  | 'cloudLocalSync'
  | 'h100s'
  | 'infiniband'
  | 'liquidCooling'
  | 'onCallEngineer'
  | 'rackDensity'
  | 'coloDeal'
  | 'nuclearPowerContract'
  | 'riverCooling'
  | 'customAsics'
  | 'acquiredStartupHardware'
  | 'centralizedTraining'
  | 'verticalIntegration'
  | 'governmentContract'
  | 'submarineCables'
  | 'orbitalDatacenters'
  | 'fusionReactor'
  | 'globalNetworkEffect'
  | 'agiEmergentBehavior'
  | 'muscleMemory'
  | 'macroKeys'
  | 'autoclickerTolerance'
  | 'neuralLink'
  // AI Wrapper upgrades
  | 'gradientLogo'
  | 'seriesAFunding'
  | 'ybDemoDay'
  | 'rateLimitIncrease'
  | 'enterpriseTier'
  | 'apiAggregator'
  // Pear Pro upgrades
  | 'switchToUsbC'
  | 'pearPodsMax'
  | 'dongleCollection'
  | 'pearSiliconUltra'
  | 'pearIntelligence'
  | 'creativeSuiteBundle'
  // TPU upgrades
  | 'cloudCreditsPromo'
  | 'proNotebookTier'
  | 'tensorFramework'
  | 'batchJobPriority'
  | 'bigDataPipeline'
  | 'mlPlatformIntegration'
  // Custom Model upgrades
  | 'fineTuningPipeline'
  | 'rlhfTraining'
  | 'syntheticData'
  | 'benchmarkGaming'
  | 'openWeightsRelease'
  | 'emergentAbilities'
  | 'distillationPipeline'
  // Atomic Energy upgrades
  | 'gridPriorityAccess'
  | 'smrFleet'
  | 'thoriumReactors'
  | 'legacyPlantRevival'
  | 'fusionBreakthrough'
  | 'datacenterPowerDeal'
  // Quantum Compute upgrades
  | 'researchLabPartnership'
  | 'supercooledChambers'
  | 'qubitScaling'
  | 'errorCorrection'
  | 'quantumSupremacy'
  | 'hybridClassical'
  // Dyson Sphere upgrades
  | 'stellarLifting'
  | 'vonNeumannProbes'
  | 'matrioshkaBrain'
  | 'ftlNetwork'
  | 'typeIIICivilization'
  | 'galacticExpansion'
  // Additional Datacenter upgrades
  | 'underwaterCenters'
  | 'spaceCooling'
  // Additional Hyperscaler upgrades
  | 'closedAIPartnership'
  | 'circularDebtFlows'

export type UpgradeEffectType =
  | { readonly type: 'doubler'; readonly targetBuilding: BuildingId | 'click' }
  | { readonly type: 'synergy'; readonly targetBuilding: BuildingId | 'click'; readonly sourceBuilding: BuildingId | 'all'; readonly bonusPercentage: number }
  | { readonly type: 'clickScaling'; readonly tpsPercentage: number }

export type UpgradeRequirement =
  | { readonly type: 'buildingCount'; readonly buildingId: BuildingId; readonly count: number }
  | { readonly type: 'clickCount'; readonly count: number }

export type UpgradeDefinition = {
  readonly id: UpgradeId
  readonly name: string
  readonly cost: Decimal
  readonly requirement: UpgradeRequirement
  readonly effect: UpgradeEffectType
  readonly description: string
}

export type GameState = {
  readonly tokens: Decimal
  readonly totalClicks: Decimal
  readonly buildings: ReadonlyMap<BuildingId, Decimal>
  readonly upgrades: ReadonlySet<UpgradeId>
  readonly lastTickMs: number
}

export type UpgradeContribution = {
  readonly upgradeId: UpgradeId
  readonly upgradeName: string
  readonly type: 'doubler' | 'synergy'
  readonly multiplierValue: Decimal
  readonly description: string
}

export type BuildingProductionBreakdown = {
  readonly buildingId: BuildingId
  readonly buildingName: string
  readonly baseProductionPerUnit: Decimal
  readonly quantity: Decimal
  readonly baseTotal: Decimal
  readonly upgradeContributions: readonly UpgradeContribution[]
  readonly totalMultiplier: Decimal
  readonly finalProduction: Decimal
  readonly percentageOfTotalTPS: Decimal
}

export type SaveData = {
  readonly version: number
  readonly tokens: string
  readonly totalClicks: string
  readonly buildings: ReadonlyArray<{ readonly id: BuildingId; readonly count: string }>
  readonly upgrades: ReadonlyArray<UpgradeId>
}

export const createInitialGameState = (): GameState => ({
  tokens: new Decimal(0),
  totalClicks: new Decimal(0),
  buildings: new Map<BuildingId, Decimal>(),
  upgrades: new Set<UpgradeId>(),
  lastTickMs: Date.now(),
})
